(() => {
  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));

  /* =========================================
     HEADER
     ========================================= */
  const initHeader = () => {
    const header = document.getElementById("mdHeader");
    if (!header) return;

    const SHOW_AT = 24;
    const pageType = document.body?.dataset?.page || "";
    const isHome = pageType === "home";
    const isInner = pageType === "inner";

    const updateHeaderVisibility = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;

      if (isHome) {
        header.classList.toggle("is-visible", y > SHOW_AT);
      } else if (isInner) {
        header.classList.add("is-visible");
      } else {
        header.classList.add("is-visible");
      }
    };

    window.addEventListener("scroll", updateHeaderVisibility, { passive: true });
    updateHeaderVisibility();
  };

  /* =========================================
     DRAWER / PANELS
     ========================================= */
  const initDrawer = () => {
    const burger = qs(".mdBurger");
    const drawer = document.getElementById("mdDrawer");
    if (!burger || !drawer) return;

    const screens = qsa("[data-drawer-screen]", drawer);
    const openScreenButtons = qsa("[data-open-screen]", drawer);
    const backButtons = qsa("[data-back-screen]", drawer);

    const showScreen = (name = "root") => {
      screens.forEach((screen) => {
        const active = screen.getAttribute("data-drawer-screen") === name;
        screen.classList.toggle("is-active", active);
      });
    };

    const setDrawer = (open) => {
      drawer.classList.toggle("is-open", open);
      drawer.setAttribute("aria-hidden", open ? "false" : "true");
      burger.setAttribute("aria-expanded", open ? "true" : "false");
      document.documentElement.style.overflow = open ? "hidden" : "";

      if (open) showScreen("root");
    };

    burger.addEventListener("click", () => {
      setDrawer(!drawer.classList.contains("is-open"));
    });

    drawer.addEventListener("click", (event) => {
      const closeTarget = event.target.closest("[data-close='1']");
      if (closeTarget) {
        setDrawer(false);
        return;
      }

      const link = event.target.closest(".mdDrawer__link[href]");
      if (link) {
        setDrawer(false);
      }
    });

    openScreenButtons.forEach((button) => {
      button.addEventListener("click", () => {
        const target = button.getAttribute("data-open-screen");
        if (target) showScreen(target);
      });
    });

    backButtons.forEach((button) => {
      button.addEventListener("click", () => showScreen("root"));
    });

    window.addEventListener("keydown", (event) => {
      if (event.key === "Escape") setDrawer(false);
    });
  };

  /* =========================================
     MOODSTRIP PARALLAX
     ========================================= */
  const initMoodstripParallax = () => {
    const strips = qsa("[data-parallax-strip]");
    if (!strips.length) return;

    let ticking = false;

    const updateParallax = () => {
      const vh = window.innerHeight;

      strips.forEach((img) => {
        const section = img.closest(".mdMoodstrip");
        if (!section) return;

        const rect = section.getBoundingClientRect();
        if (rect.bottom <= 0 || rect.top >= vh) return;

        const sectionCenter = rect.top + rect.height / 2;
        const viewportCenter = vh / 2;
        const distance = sectionCenter - viewportCenter;
        const offset = distance * -0.18;

        img.style.transform = `translate(-50%, calc(-50% + ${offset}px))`;
      });

      ticking = false;
    };

    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateParallax);
    };

    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
    window.addEventListener("load", requestUpdate);
    requestUpdate();
  };

  /* =========================================
     EXPERIENCES
     ========================================= */
  const initExperiences = () => {
    const expSection = qs(".mdExperiences");
    const expTrack = qs("[data-exp-track]");
    const expPrev = qs("[data-exp-prev]");
    const expNext = qs("[data-exp-next]");

    if (!expSection || !expTrack) return;

    const cards = qsa(".mdExperienceCard", expTrack);
    const total = cards.length;
    let index = 0;

    expSection.setAttribute("data-count", String(total));

    const isDesktop = () => window.matchMedia("(min-width: 901px)").matches;
    const isCarousel = () => isDesktop() && total > 3;
    const maxIndex = () => Math.max(0, total - 3);

    const updateArrows = () => {
      if (!expPrev || !expNext) return;

      const carousel = isCarousel();
      expSection.classList.toggle("is-carousel", carousel);

      expPrev.hidden = !carousel;
      expNext.hidden = !carousel;

      if (!carousel) {
        expPrev.disabled = true;
        expNext.disabled = true;
        return;
      }

      expPrev.disabled = index <= 0;
      expNext.disabled = index >= maxIndex();
    };

    const updateSlider = () => {
      if (!isCarousel()) {
        index = 0;
        expTrack.style.transform = "translate3d(0,0,0)";
        updateArrows();
        return;
      }

      const step = 100 / 3;
      expTrack.style.transform = `translate3d(-${index * step}%,0,0)`;
      updateArrows();
    };

    if (expPrev) {
      expPrev.addEventListener("click", () => {
        if (!isCarousel()) return;
        index = Math.max(0, index - 1);
        updateSlider();
      });
    }

    if (expNext) {
      expNext.addEventListener("click", () => {
        if (!isCarousel()) return;
        index = Math.min(maxIndex(), index + 1);
        updateSlider();
      });
    }

    window.addEventListener("resize", updateSlider);
    window.addEventListener("load", updateSlider);
    updateSlider();
  };

  /* =========================================
     BENEFIT MEDIA
     ========================================= */
  const initBenefitMedia = () => {
    const sections = qsa(".mdBenefitMedia");
    if (!sections.length) return;

    sections.forEach((benefitMedia) => {
      const steps = qsa("[data-benefit-step]", benefitMedia);
      const targetImg = qs("[data-benefit-target]", benefitMedia);

      if (!steps.length || !targetImg) return;

      let switching = false;

      const activateStep = (step) => {
        if (!step || switching) return;

        const nextSrc = step.getAttribute("data-benefit-image");
        const nextAlt = step.getAttribute("data-benefit-alt") || "";

        if (!nextSrc) return;

        steps.forEach((btn) => {
          const active = btn === step;
          btn.classList.toggle("is-active", active);
          btn.setAttribute("aria-selected", active ? "true" : "false");
        });

        if (targetImg.getAttribute("src") === nextSrc) return;

        switching = true;
        targetImg.classList.add("is-switching");

        const preload = new Image();
        preload.src = nextSrc;

        preload.onload = () => {
          window.setTimeout(() => {
            targetImg.src = nextSrc;
            targetImg.alt = nextAlt;

            window.setTimeout(() => {
              targetImg.classList.remove("is-switching");
              switching = false;
            }, 40);
          }, 160);
        };

        preload.onerror = () => {
          targetImg.classList.remove("is-switching");
          switching = false;
        };
      };

      steps.forEach((step) => {
        step.addEventListener("click", () => activateStep(step));
        step.addEventListener("mouseenter", () => {
          if (window.matchMedia("(hover:hover) and (pointer:fine)").matches) {
            activateStep(step);
          }
        });
      });
    });
  };

  /* =========================================
     PHOTO STRIP
     ========================================= */
  const initPhotoStrips = () => {
    const photoStrips = qsa("[data-photo-strip]");
    if (!photoStrips.length) return;

    photoStrips.forEach((strip) => {
      const viewport = qs("[data-photo-strip-viewport]", strip);
      const track = qs("[data-photo-strip-track]", strip);
      const items = qsa(".mdPhotoStrip__item", strip);

      if (!viewport || !track || !items.length) return;

      const originalCount = Math.floor(items.length / 2) || items.length;
      const speed = Number(strip.getAttribute("data-strip-speed")) || 18;

      let rafId = null;
      let lastTs = 0;
      let currentX = 0;
      let paused = false;
      let isDragging = false;
      let dragStartX = 0;
      let startTranslateX = 0;
      let activeItem = null;

      const getLoopWidth = () => {
        const originals = items.slice(0, originalCount);
        return originals.reduce((sum, item) => sum + item.offsetWidth, 0);
      };

      const normalizePosition = () => {
        const loopWidth = getLoopWidth();
        if (!loopWidth) return;

        while (currentX <= -loopWidth) currentX += loopWidth;
        while (currentX > 0) currentX -= loopWidth;
      };

      const render = () => {
        track.style.transform = `translate3d(${currentX}px,0,0)`;
      };

      const tick = (ts) => {
        if (!lastTs) lastTs = ts;
        const dt = (ts - lastTs) / 1000;
        lastTs = ts;

        if (!paused && !isDragging) {
          currentX -= speed * dt;
          normalizePosition();
          render();
        }

        rafId = window.requestAnimationFrame(tick);
      };

      const start = () => {
        if (rafId) return;
        rafId = window.requestAnimationFrame(tick);
      };

      const pause = () => {
        paused = true;
        strip.classList.add("is-paused");
      };

      const resume = () => {
        paused = false;
        strip.classList.remove("is-paused");
      };

      const clearActiveItem = () => {
        if (activeItem) {
          activeItem.classList.remove("is-active");
          activeItem = null;
        }
      };

      const setActiveItem = (item) => {
        if (activeItem && activeItem !== item) {
          activeItem.classList.remove("is-active");
        }
        activeItem = item;
        if (activeItem) activeItem.classList.add("is-active");
      };

      if (window.matchMedia("(hover:hover) and (pointer:fine)").matches) {
        strip.addEventListener("mouseenter", pause);
        strip.addEventListener("mouseleave", () => {
          clearActiveItem();
          resume();
        });

        items.forEach((item) => {
          item.addEventListener("mouseenter", () => setActiveItem(item));
          item.addEventListener("focusin", () => {
            pause();
            setActiveItem(item);
          });
          item.addEventListener("focusout", () => {
            clearActiveItem();
            resume();
          });
        });
      }

      const getPointX = (event) => {
        if (event.touches?.[0]) return event.touches[0].clientX;
        if (event.changedTouches?.[0]) return event.changedTouches[0].clientX;
        return event.clientX;
      };

      const onDragStart = (event) => {
        isDragging = true;
        pause();
        viewport.classList.add("is-dragging");
        dragStartX = getPointX(event);
        startTranslateX = currentX;

        const item = event.target.closest(".mdPhotoStrip__item");
        if (item) setActiveItem(item);
      };

      const onDragMove = (event) => {
        if (!isDragging) return;
        const currentPointerX = getPointX(event);
        const delta = currentPointerX - dragStartX;
        currentX = startTranslateX + delta;
        normalizePosition();
        render();
      };

      const onDragEnd = () => {
        if (!isDragging) return;
        isDragging = false;
        viewport.classList.remove("is-dragging");

        window.setTimeout(() => {
          clearActiveItem();
          resume();
        }, 220);
      };

      viewport.addEventListener("touchstart", onDragStart, { passive: true });
      viewport.addEventListener("touchmove", onDragMove, { passive: true });
      viewport.addEventListener("touchend", onDragEnd);
      viewport.addEventListener("touchcancel", onDragEnd);

      viewport.addEventListener("mousedown", onDragStart);
      window.addEventListener("mousemove", onDragMove);
      window.addEventListener("mouseup", onDragEnd);

      window.addEventListener("resize", () => {
        normalizePosition();
        render();
      });

      render();
      start();
    });
  };

  /* =========================================
     SIYATI PRODUCT
     ========================================= */
  const initSiyati = () => {
    const siyati = qs(".mdSiyati");
    if (!siyati) return;

    const mainImg = qs("[data-siyati-main]", siyati);
    const thumbs = qsa("[data-siyati-thumb]", siyati);

    thumbs.forEach((thumb) => {
      thumb.addEventListener("click", () => {
        const nextSrc = thumb.getAttribute("data-siyati-thumb");
        if (!nextSrc || !mainImg) return;

        mainImg.src = nextSrc;
        thumbs.forEach((btn) => btn.classList.remove("is-active"));
        thumb.classList.add("is-active");
      });
    });

  

    const accordions = qsa(".mdSiyatiAccordion", siyati);
    accordions.forEach((item) => {
      const trigger = qs(".mdSiyatiAccordion__header", item);
      if (!trigger) return;

      trigger.addEventListener("click", () => {
        item.classList.toggle("is-open");
      });
    });
  };

  (() => {
  const SELECTOR_ROOT = "[data-md-wording-accordion]";
  const SELECTOR_GROUP = "[data-md-wording-accordion-group]";
  const SELECTOR_ITEM = "[data-md-wording-item]";
  const SELECTOR_TRIGGER = "[data-md-wording-trigger]";
  const SELECTOR_PANEL = "[data-md-wording-panel]";

  function closeItem(item, animated = true) {
    const trigger = item.querySelector(SELECTOR_TRIGGER);
    const panel = item.querySelector(SELECTOR_PANEL);
    if (!trigger || !panel) return;

    item.classList.remove("is-open");
    trigger.setAttribute("aria-expanded", "false");

    const endClose = () => {
      panel.hidden = true;
      panel.style.height = "";
      panel.style.overflow = "";
      panel.style.transition = "";
      panel.removeEventListener("transitionend", endClose);
    };

    if (!animated) {
      panel.hidden = true;
      panel.style.height = "";
      panel.style.overflow = "";
      panel.style.transition = "";
      return;
    }

    panel.hidden = false;
    panel.style.height = `${panel.scrollHeight}px`;
    panel.style.overflow = "hidden";
    panel.style.transition = "height 320ms ease";

    requestAnimationFrame(() => {
      panel.style.height = "0px";
    });

    panel.addEventListener("transitionend", endClose);
  }

  function openItem(item, animated = true) {
    const trigger = item.querySelector(SELECTOR_TRIGGER);
    const panel = item.querySelector(SELECTOR_PANEL);
    if (!trigger || !panel) return;

    item.classList.add("is-open");
    trigger.setAttribute("aria-expanded", "true");
    panel.hidden = false;

    const endOpen = () => {
      panel.style.height = "auto";
      panel.style.overflow = "";
      panel.style.transition = "";
      panel.removeEventListener("transitionend", endOpen);
    };

    if (!animated) {
      panel.style.height = "auto";
      panel.style.overflow = "";
      panel.style.transition = "";
      return;
    }

    panel.style.height = "0px";
    panel.style.overflow = "hidden";
    panel.style.transition = "height 320ms ease";

    requestAnimationFrame(() => {
      panel.style.height = `${panel.scrollHeight}px`;
    });

    panel.addEventListener("transitionend", endOpen);
  }

  function closeOthers(group, currentItem) {
    const items = group.querySelectorAll(SELECTOR_ITEM);
    items.forEach((item) => {
      if (item !== currentItem && item.classList.contains("is-open")) {
        closeItem(item, true);
      }
    });
  }

  function onTriggerClick(event) {
    const trigger = event.target.closest(SELECTOR_TRIGGER);
    if (!trigger) return;

    const root = trigger.closest(SELECTOR_ROOT);
    const group = trigger.closest(SELECTOR_GROUP);
    const item = trigger.closest(SELECTOR_ITEM);

    if (!root || !group || !item) return;

    const isOpen = item.classList.contains("is-open");

    if (isOpen) {
      closeItem(item, true);
      return;
    }

    closeOthers(group, item);
    openItem(item, true);
  }

  function onTriggerKeydown(event) {
    const trigger = event.target.closest(SELECTOR_TRIGGER);
    if (!trigger) return;

    const group = trigger.closest(SELECTOR_GROUP);
    if (!group) return;

    const triggers = Array.from(group.querySelectorAll(SELECTOR_TRIGGER));
    const currentIndex = triggers.indexOf(trigger);
    if (currentIndex === -1) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      triggers[(currentIndex + 1) % triggers.length].focus();
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      triggers[(currentIndex - 1 + triggers.length) % triggers.length].focus();
    }

    if (event.key === "Home") {
      event.preventDefault();
      triggers[0].focus();
    }

    if (event.key === "End") {
      event.preventDefault();
      triggers[triggers.length - 1].focus();
    }
  }

  function prepareGroup(group) {
    if (!group || group.dataset.mdWordingReady === "true") return;
    group.dataset.mdWordingReady = "true";

    const items = Array.from(group.querySelectorAll(SELECTOR_ITEM));
    const openFirst = group.dataset.openFirst === "true";

    items.forEach((item, index) => {
      const trigger = item.querySelector(SELECTOR_TRIGGER);
      const panel = item.querySelector(SELECTOR_PANEL);
      if (!trigger || !panel) return;

      const shouldOpen = openFirst && index === 0;

      item.classList.toggle("is-open", shouldOpen);
      trigger.setAttribute("aria-expanded", shouldOpen ? "true" : "false");

      if (shouldOpen) {
        panel.hidden = false;
        panel.style.height = "auto";
      } else {
        panel.hidden = true;
        panel.style.height = "0px";
      }
    });

    group.addEventListener("click", onTriggerClick);
    group.addEventListener("keydown", onTriggerKeydown);
  }

  function initMdWordingAccordion(scope = document) {
    const groups = scope.querySelectorAll(SELECTOR_GROUP);
    groups.forEach(prepareGroup);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initMdWordingAccordion(document);
    });
  } else {
    initMdWordingAccordion(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    initMdWordingAccordion(event.target);
  });
})();


 /* =========================================
     FAQ
     ========================================= */

(() => {
  const SELECTOR_ROOT = "[data-md-faq]";
  const SELECTOR_GROUP = "[data-md-faq-group]";
  const SELECTOR_ITEM = "[data-md-faq-item]";
  const SELECTOR_TRIGGER = "[data-md-faq-trigger]";
  const SELECTOR_PANEL = "[data-md-faq-panel]";

  function closeItem(item, animated = true) {
    const trigger = item.querySelector(SELECTOR_TRIGGER);
    const panel = item.querySelector(SELECTOR_PANEL);
    if (!trigger || !panel) return;

    item.classList.remove("is-open");
    trigger.setAttribute("aria-expanded", "false");

    const endClose = (event) => {
      if (event && event.target !== panel) return;
      panel.hidden = true;
      panel.style.height = "";
      panel.style.overflow = "";
      panel.style.transition = "";
      panel.removeEventListener("transitionend", endClose);
    };

    if (!animated) {
      panel.hidden = true;
      panel.style.height = "";
      panel.style.overflow = "";
      panel.style.transition = "";
      return;
    }

    panel.hidden = false;
    panel.style.height = `${panel.scrollHeight}px`;
    panel.style.overflow = "hidden";
    panel.style.transition = "height 320ms ease";

    requestAnimationFrame(() => {
      panel.style.height = "0px";
    });

    panel.addEventListener("transitionend", endClose);
  }

  function openItem(item, animated = true) {
    const trigger = item.querySelector(SELECTOR_TRIGGER);
    const panel = item.querySelector(SELECTOR_PANEL);
    if (!trigger || !panel) return;

    item.classList.add("is-open");
    trigger.setAttribute("aria-expanded", "true");
    panel.hidden = false;

    const endOpen = (event) => {
      if (event && event.target !== panel) return;
      panel.style.height = "auto";
      panel.style.overflow = "";
      panel.style.transition = "";
      panel.removeEventListener("transitionend", endOpen);
    };

    if (!animated) {
      panel.style.height = "auto";
      panel.style.overflow = "";
      panel.style.transition = "";
      return;
    }

    panel.style.height = "0px";
    panel.style.overflow = "hidden";
    panel.style.transition = "height 320ms ease";

    requestAnimationFrame(() => {
      panel.style.height = `${panel.scrollHeight}px`;
    });

    panel.addEventListener("transitionend", endOpen);
  }

  function closeOthers(group, currentItem) {
    const items = group.querySelectorAll(SELECTOR_ITEM);
    items.forEach((item) => {
      if (item !== currentItem && item.classList.contains("is-open")) {
        closeItem(item, true);
      }
    });
  }

  function onTriggerClick(event) {
    const trigger = event.target.closest(SELECTOR_TRIGGER);
    if (!trigger) return;

    const root = trigger.closest(SELECTOR_ROOT);
    const group = trigger.closest(SELECTOR_GROUP);
    const item = trigger.closest(SELECTOR_ITEM);

    if (!root || !group || !item) return;

    const isOpen = item.classList.contains("is-open");

    if (isOpen) {
      closeItem(item, true);
      return;
    }

    closeOthers(group, item);
    openItem(item, true);
  }

  function onTriggerKeydown(event) {
    const trigger = event.target.closest(SELECTOR_TRIGGER);
    if (!trigger) return;

    const group = trigger.closest(SELECTOR_GROUP);
    if (!group) return;

    const triggers = Array.from(group.querySelectorAll(SELECTOR_TRIGGER));
    const currentIndex = triggers.indexOf(trigger);
    if (currentIndex === -1) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      triggers[(currentIndex + 1) % triggers.length].focus();
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      triggers[(currentIndex - 1 + triggers.length) % triggers.length].focus();
    }

    if (event.key === "Home") {
      event.preventDefault();
      triggers[0].focus();
    }

    if (event.key === "End") {
      event.preventDefault();
      triggers[triggers.length - 1].focus();
    }
  }

  function prepareGroup(group) {
    if (!group || group.dataset.mdFaqReady === "true") return;
    group.dataset.mdFaqReady = "true";

    const items = Array.from(group.querySelectorAll(SELECTOR_ITEM));
    const openFirst = group.dataset.openFirst === "true";

    items.forEach((item, index) => {
      const trigger = item.querySelector(SELECTOR_TRIGGER);
      const panel = item.querySelector(SELECTOR_PANEL);
      if (!trigger || !panel) return;

      const shouldOpen = openFirst && index === 0;

      item.classList.toggle("is-open", shouldOpen);
      trigger.setAttribute("aria-expanded", shouldOpen ? "true" : "false");

      if (shouldOpen) {
        panel.hidden = false;
        panel.style.height = "auto";
      } else {
        panel.hidden = true;
        panel.style.height = "0px";
      }
    });

    group.addEventListener("click", onTriggerClick);
    group.addEventListener("keydown", onTriggerKeydown);
  }

  function initMdFaq(scope = document) {
    const groups = scope.querySelectorAll(SELECTOR_GROUP);
    groups.forEach(prepareGroup);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initMdFaq(document);
    });
  } else {
    initMdFaq(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    initMdFaq(event.target);
  });
})();

  /* =========================================
     BOOT
     ========================================= */
  const initAll = () => {
  initHeader();
  initDrawer();
  initMoodstripParallax();
  initExperiences();
  initBenefitMedia();
  initPhotoStrips();
  initSiyati();
};

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll, { once: true });
  } else {
    initAll();
  }
})();


/* =========================================================
   MAISON DAGA — ORDER DRAWER (GSAP)
   ========================================================= */

document.addEventListener('DOMContentLoaded', () => {
  const orderDrawer = document.querySelector('.mdOrderDrawer');
  if (!orderDrawer || typeof gsap === 'undefined') return;

  const overlay = orderDrawer.querySelector('.mdOrderDrawer__overlay');
  const panel = orderDrawer.querySelector('.mdOrderDrawer__panel');
  const closeBtn = orderDrawer.querySelector('.mdOrderDrawer__close');

  const openTriggers = [
    document.getElementById('mdCheckoutTrigger'),
    document.getElementById('mdReviewTrigger'),
    document.getElementById('mdConfirmOrderOptions')
  ].filter(Boolean);

  const closeTriggers = [
    overlay,
    closeBtn
  ].filter(Boolean);

  let isAnimating = false;

  function openOrderDrawer() {
    if (isAnimating || orderDrawer.classList.contains('is-open')) return;
    isAnimating = true;

    orderDrawer.classList.add('is-open');
    document.body.classList.add('mdOrderOpen');

    gsap.set(overlay, { opacity: 0 });
    gsap.set(panel, { x: 64, opacity: 0.96 });

    const tl = gsap.timeline({
      defaults: { ease: 'power3.out' },
      onComplete: () => { isAnimating = false; }
    });

    tl.to(overlay, { opacity: 1, duration: 0.28 }, 0);
    tl.to(panel, { x: 0, opacity: 1, duration: 0.42 }, 0.02);
  }

  function closeOrderDrawer() {
    if (isAnimating || !orderDrawer.classList.contains('is-open')) return;
    isAnimating = true;

    const tl = gsap.timeline({
      defaults: { ease: 'power2.inOut' },
      onComplete: () => {
        orderDrawer.classList.remove('is-open');
        document.body.classList.remove('mdOrderOpen');
        isAnimating = false;
      }
    });

    tl.to(panel, { x: 56, opacity: 0.96, duration: 0.28 }, 0);
    tl.to(overlay, { opacity: 0, duration: 0.24 }, 0.04);
  }

  openTriggers.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      openOrderDrawer();
    });
  });

  closeTriggers.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      closeOrderDrawer();
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && orderDrawer.classList.contains('is-open')) {
      closeOrderDrawer();
    }
  });
});